/**
 * ImageUpload — file picker + uploader that links the upload to an entity.
 *
 * Used on detail pages to let the user attach an image (vehicle, animal,
 * pasture, …). On success the parent re-renders so the new image shows up.
 *
 * Validation lives both client and server side; the client checks size
 * and MIME up-front so the user gets immediate feedback rather than a
 * round-trip to the worker.
 */
import { useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { ImagePlus, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { uploadAttachment } from '@/lib/attachments';

interface ImageUploadProps {
  entityType: string;
  entityId: string;
  onUploaded?: () => void;
  /** Visible button label. Defaults to "Add image". */
  label?: string;
  /** Compact mode renders a smaller icon-only button. */
  compact?: boolean;
  className?: string;
}

const ACCEPT = 'image/jpeg,image/png,image/webp,image/heic';
const MAX_BYTES = 20 * 1024 * 1024;

export function ImageUpload({
  entityType,
  entityId,
  onUploaded,
  label,
  compact,
  className,
}: ImageUploadProps): JSX.Element {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState<boolean>(false);

  const handlePick = (): void => {
    inputRef.current?.click();
  };

  const handleChange = async (e: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;

    if (file.size > MAX_BYTES) {
      toast.error(`Image too large (max 20 MB; this one is ${(file.size / 1024 / 1024).toFixed(1)} MB)`);
      return;
    }
    if (!ACCEPT.split(',').includes(file.type)) {
      toast.error('Unsupported image format. Use JPEG, PNG, WebP, or HEIC.');
      return;
    }

    setUploading(true);
    try {
      await uploadAttachment(file, { linked_entity_type: entityType, linked_entity_id: entityId });
      toast.success('Image uploaded');
      onUploaded?.();
    } catch (err) {
      toast.error((err as Error).message ?? 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        onChange={handleChange}
        className="hidden"
        aria-hidden="true"
      />
      <Button
        type="button"
        variant="outline"
        size={compact ? 'icon' : 'sm'}
        onClick={handlePick}
        disabled={uploading}
        aria-label={label ?? 'Add image'}
        className={className}
      >
        {uploading ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <ImagePlus className="h-4 w-4" />
        )}
        {compact ? null : <span className="ml-2">{uploading ? 'Uploading…' : (label ?? 'Add image')}</span>}
      </Button>
    </>
  );
}
